import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { LoadingOutlined } from "@ant-design/icons";
import { Button } from "@/shared/components/button";
import { PillTabs } from "@/shared/components/PillTabs";
import { cn } from "@/shared/lib/utils";
import type { Medication } from "@/shared/types";
import type { ReviewItem } from "@/portals/patient/api/reviewItemsApi";
import { ANSWER_LABELS, itemConversation, toMedications } from "@/features/patient/medications/components/ReviewItemView";
import { PatientConversation } from "@/features/patient/medications/components/PatientConversation";
import { RxcuiTag } from "@/features/patient/medications/components/RxcuiTag";
import type { ReviewProcessing } from "@/portals/patient/api/reviewItemsApi";
import { ReviewProcessingBanner } from "./ReviewProcessingBanner";
import { ISSUE_LABELS, SourceBadge } from "@/features/patient/medications/components/medicationReviewBadges";
import { isPairItem, reviewTitle } from "@/features/patient/medications/components/ReviewConflictCard";

/**
 * Human Review Queue — the patient's medication review items (Module 2/3),
 * in the HRQ's own design. Every item waiting on the patient is a Flagged
 * Record; the patient either reviews it (the review modal: answer + comment)
 * or skips it, and either way it goes on to their clinician / the shared
 * queue. Items they answered are Reviewed; items they skipped — or that moved
 * on by themselves because no answer came in time — are Skipped.
 */

interface HumanReviewQueueSectionProps {
  items: ReviewItem[];
  /** Opens the review modal for this item. */
  onReview: (itemId: string) => void;
  /** Sends the item to the clinician without an answer or comment. */
  onSkip: (itemId: string) => void;
  /** Item whose skip is in flight. */
  skippingId?: string | null;
  /** Opens with this item selected, on its tab (a notification link's ?item=). */
  focusItemId?: string | null;
  /** New records still being read / checked — shows the progress banner. */
  processing?: ReviewProcessing | null;
  /** The work just finished: show every step done and the outcome for a moment. */
  justFinished?: boolean;
}

type TabKey = "flagged" | "reviewed" | "skipped";

/** How many of the latest answered/skipped items Recently Reviewed shows. */
const RECENT_LIMIT = 15;

function lastResponse(item: ReviewItem) {
  return item.patientResponses[item.patientResponses.length - 1];
}

export function hrqTab(item: ReviewItem): TabKey {
  if (item.awaitingInput) return "flagged";
  const last = lastResponse(item);
  return last && last.answer !== "SKIPPED" ? "reviewed" : "skipped";
}

function daysSince(iso: string): number {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

function formatDaysAgo(days: number): string {
  if (days <= 0) return "Today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

function formatDate(dateString: string): string {
  if (!dateString) return "Not available";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const date = new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return "Not available";
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/** The medicine the item is about: the newer record of a pair, else the one record. */
function subject(meds: Medication[]): Medication {
  return meds.find((m) => m.review?.recordRole === "INCOMING") ?? meds[0];
}

/** Where an item stands once it left the patient's hands. */
function outcomeTag(item: ReviewItem): { label: string; className: string } {
  if (item.itemStatus === "RESOLVED_CLINICIAN_VERIFIED") return { label: "Decided", className: "bg-[#DCFCE7] text-[#16A34A]" };
  if (item.itemStatus === "RESOLVED_PATIENT_REPORTED") return { label: "Closed", className: "bg-[#DCFCE7] text-[#16A34A]" };
  return { label: "With clinician", className: "bg-[#EEF2F7] text-[#0A2947]" };
}

/** "You answered: …" — or, for a plain reply to a question, that you replied. */
function answeredText(answer: ReviewItem["patientResponses"][number]["answer"]): string {
  return answer === "REPLY" ? "You replied to your clinician" : `You answered: ${ANSWER_LABELS[answer]}`;
}

function skippedText(item: ReviewItem): string {
  return lastResponse(item)
    ? "You skipped this — your clinician decides without your input."
    : "No answer came in time, so it moved to your clinician automatically.";
}

interface Row {
  item: ReviewItem;
  meds: Medication[];
  med: Medication;
  tab: TabKey;
}

export function HumanReviewQueueSection({ items, onReview, onSkip, skippingId, focusItemId, processing, justFinished }: HumanReviewQueueSectionProps) {
  const busy = Boolean(processing && processing.state !== "idle");
  const [activeTab, setActiveTab] = useState<TabKey>("flagged");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // A notification link (?item=) opens that item: switch to its tab and select
  // it — once per tab it lands on, so fresh data that moves it (e.g. the list
  // first shown from the page cache was older) still opens it in the right place.
  const focusedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusItemId) return;
    const target = items.find((i) => i.id === focusItemId);
    if (!target) return;
    const tab = hrqTab(target);
    if (focusedRef.current === `${focusItemId}:${tab}`) return;
    focusedRef.current = `${focusItemId}:${tab}`;
    setActiveTab(tab);
    setSelectedId(focusItemId);
  }, [focusItemId, items]);

  const rows = useMemo<Row[]>(
    () =>
      items
        .filter((item) => item.records.length > 0)
        .map((item) => {
          const meds = toMedications(item);
          return { item, meds, med: subject(meds), tab: hrqTab(item) };
        }),
    [items],
  );

  const flagged = rows.filter((r) => r.tab === "flagged");
  const reviewed = rows.filter((r) => r.tab === "reviewed");
  const skipped = rows.filter((r) => r.tab === "skipped");
  const listForTab = activeTab === "flagged" ? flagged : activeTab === "reviewed" ? reviewed : skipped;
  // The first record of the tab is selected unless the patient picked (or closed) one.
  const selected =
    selectedId === "" ? null : listForTab.find((r) => r.item.id === selectedId) ?? listForTab[0] ?? null;

  // The 15 most recent the patient dealt with (answered, skipped, or moved on unanswered).
  const recent = [...reviewed, ...skipped]
    .map((r) => ({ ...r, at: lastResponse(r.item)?.respondedAt ?? r.item.createdAt }))
    .sort((a, b) => b.at.localeCompare(a.at))
    .slice(0, RECENT_LIMIT);

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    setSelectedId(null);
  };

  const tabs: { key: TabKey; label: string; count: number }[] = [
    { key: "flagged", label: "Flagged Records", count: flagged.length },
    { key: "reviewed", label: "Reviewed", count: reviewed.length },
    { key: "skipped", label: "Skipped", count: skipped.length },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {justFinished ? (
        <ReviewProcessingBanner processing={processing} finished={{ needsReview: flagged.length }} />
      ) : (
        processing && <ReviewProcessingBanner processing={processing} />
      )}

      {/* The platform's pill splitter (same as the clinician dashboard / Document Panel). */}
      <div className="mb-5 flex justify-center">
        <PillTabs
          label="Review queue view"
          active={activeTab}
          onChange={handleTabChange}
          tabs={tabs.map((tab) => ({ id: tab.key, label: `${tab.label} (${tab.count})` }))}
        />
      </div>

      {/* Side by side only when there's room next to the sidebar (xl); stacked on iPad. */}
      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
        {/* Left — record list */}
        <div className="grid gap-5">
          {listForTab.length === 0 ? (
            // Same height and centring as the empty detail card beside it.
            <div className="flex min-h-[300px] items-center justify-center rounded-2xl border border-[#E5E7EB] bg-white p-8 text-center text-sm text-[#6B7280]">
              {activeTab === "flagged"
                ? busy
                  ? "Nothing to review yet — we're still checking your new records."
                  : "You're all caught up! Nothing left to review."
                : `No ${activeTab} records yet.`}
            </div>
          ) : (
            listForTab.map(({ item, med }) => {
              const isSelected = item.id === selected?.item.id;
              return (
                <Button
                  key={item.id}
                  variant="outline"
                  data-testid="hrq-row"
                  onClick={() => setSelectedId(item.id)}
                  className={cn(
                    "!block !h-auto !w-full !whitespace-normal !rounded-xl !px-4 !py-3 !text-left !shadow-sm",
                    isSelected
                      ? "!border-2 !border-[#F17893] !bg-[#FFF2F4] hover:!border-[#F17893] hover:!bg-[#FFF2F4]"
                      : "!border-[#E5E7EB] !bg-white hover:!bg-[#FFF8F8] hover:!shadow-md",
                  )}
                >
                  <div className="flex min-w-0 items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <h3 className="min-w-0 break-words text-sm font-bold text-[#0A2947]">{med.name}</h3>
                      {med.dosage !== "Not specified" && (
                        <span className="shrink-0 rounded-md bg-[#E4002B]/70 px-2 py-0.5 text-[10px] font-bold text-white">{med.dosage}</span>
                      )}
                      <RxcuiTag rxcui={med.rxcui} />
                    </div>
                    {med.status === "active" ? (
                      <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-[#16A34A]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#16A34A]" />
                        Active
                      </span>
                    ) : (
                      <span className="shrink-0 text-xs font-medium text-[#9CA3AF]">Past</span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-[#6B7280]">{ISSUE_LABELS[item.triggerType]}</p>
                  {item.clinicianQuestion && (
                    <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#FEF6DC] px-2 py-0.5 text-[11px] font-bold text-[#946200]">
                      <Icon icon="lucide:message-circle-question" className="h-3 w-3" />
                      Your clinician asked a question
                    </span>
                  )}
                </Button>
              );
            })
          )}
        </div>

        {/* Right — detail + recently reviewed. Sticky so it stays in view while
            scrolling a long left-hand list. */}
        <div className="sticky top-5">
          {selected ? (
            <DetailCard
              row={selected}
              onClose={() => setSelectedId("")}
              onReview={onReview}
              onSkip={onSkip}
              skipping={skippingId === selected.item.id}
            />
          ) : (
            <div className="flex min-h-[300px] items-center justify-center rounded-2xl border border-[#E5E7EB] bg-white p-8 text-center text-sm text-[#6B7280]">
              {rows.length === 0 ? "Nothing to review yet." : "Select a record from the list to review it."}
            </div>
          )}

          {recent.length > 0 && (
            <section aria-label="Recently Reviewed" className="mt-5 rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
              <h3 className="mb-3 text-sm font-extrabold text-[#0A2947]">
                Recently Reviewed <span className="font-semibold text-[#9CA3AF]">({recent.length})</span>
              </h3>
              {/* About three rows visible; the rest scroll inside the card. */}
              <div data-testid="hrq-recent-list" className="-mr-2 grid max-h-[252px] gap-3 overflow-y-auto pr-2">
                {recent.map(({ item, med, tab, at }) => {
                  const answered = tab === "reviewed";
                  const tag = answered ? outcomeTag(item) : { label: "Skipped", className: "bg-[#F3F4F6] text-[#6B7280]" };
                  const last = lastResponse(item);
                  return (
                    <div key={item.id} data-testid="hrq-recent" className="flex items-center gap-3 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3">
                      <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", answered ? "bg-[#DCFCE7]" : "bg-[#F3F4F6]")}>
                        <Icon
                          icon={answered ? "lucide:check" : "lucide:skip-forward"}
                          className={cn("h-5 w-5", answered ? "text-[#16A34A]" : "text-[#6B7280]")}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-[#9CA3AF]">Medication</p>
                        <p className="truncate text-sm font-bold text-[#0A2947]">{med.name}</p>
                        <p className="truncate text-xs text-[#6B7280]">
                          {answered && last ? answeredText(last.answer) : skippedText(item)}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <span className={cn("inline-block rounded-full px-3 py-1 text-xs font-semibold", tag.className)}>{tag.label}</span>
                        <p className="mt-1 text-[11px] text-[#9CA3AF]">{formatDaysAgo(daysSince(at))}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

/** The fields shown for a record — one list, so a single record and a pair look the same. */
function recordFields(med: Medication): { label: string; value: string }[] {
  return [
    { label: "Medication", value: med.name },
    { label: "Dosage", value: med.dosage },
    { label: "Frequency", value: med.frequency },
    { label: "Route", value: med.route === "oral" ? "By Mouth" : med.route === "other" ? "Not specified" : med.route },
    { label: "Prescribed By", value: med.prescribedBy },
    { label: "Start Date", value: formatDate(med.startDate) },
  ];
}

const NOT_GIVEN = new Set(["", "not specified", "not available", "unknown provider"]);
const comparable = (value: string) => value.trim().toLowerCase().replace(/\s+/g, " ");

/** Field labels whose values differ between two records (a value missing on one side isn't a difference). */
function differingFields(a: Medication, b: Medication): Set<string> {
  const other = new Map(recordFields(b).map((f) => [f.label, comparable(f.value)]));
  return new Set(
    recordFields(a)
      .filter((f) => {
        const va = comparable(f.value);
        const vb = other.get(f.label) ?? "";
        return !NOT_GIVEN.has(va) && !NOT_GIVEN.has(vb) && va !== vb;
      })
      .map((f) => f.label),
  );
}

function FieldGrid({ med, columns, differs }: { med: Medication; columns: string; differs?: Set<string> }) {
  return (
    <div className={cn("grid gap-x-4 gap-y-4", columns)}>
      {recordFields(med).map((field) => {
        const isDifferent = differs?.has(field.label) ?? false;
        return (
          <div
            key={field.label}
            data-differs={isDifferent || undefined}
            // A conflicting field is highlighted (same amber as before), so it's the first thing seen.
            className={isDifferent ? "-m-1.5 rounded-lg bg-[#FEF6DC] p-1.5" : undefined}
          >
            <p className={cn("text-xs", isDifferent ? "font-semibold text-[#946200]" : "text-[#9CA3AF]")}>{field.label}</p>
            <p className={cn("mt-0.5 text-sm", isDifferent ? "font-extrabold text-[#0A2947]" : "font-bold text-[#111827]")}>{field.value}</p>
          </div>
        );
      })}
    </div>
  );
}

function PairFields({ meds }: { meds: Medication[] }) {
  const existing = meds.find((m) => m.review?.recordRole === "EXISTING")!;
  const incoming = meds.find((m) => m.review?.recordRole === "INCOMING")!;
  const differs = differingFields(existing, incoming);
  return (
    <div className="grid gap-3">
      {differs.size > 0 && (
        <p className="text-xs text-[#6B7280]">
          <span className="rounded bg-[#FEF6DC] px-1 font-semibold text-[#946200]">Highlighted</span> fields are different between the two records.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {[
          { title: "Earlier record", med: existing },
          { title: "Newer record", med: incoming },
        ].map(({ title, med }) => (
          <div key={title} data-testid="review-record" className="rounded-xl border border-[#E5E7EB] bg-white p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-extrabold text-[#0A2947]">{title}</p>
              <div className="flex items-center gap-1.5">
                <RxcuiTag rxcui={med.rxcui} />
                <SourceBadge vendor={med.sourceVendor} />
              </div>
            </div>
            <FieldGrid med={med} columns="grid-cols-2" differs={differs} />
          </div>
        ))}
      </div>
    </div>
  );
}

function DetailCard({ row, onClose, onReview, onSkip, skipping }: {
  row: Row; onClose: () => void; onReview: (id: string) => void; onSkip: (id: string) => void; skipping: boolean;
}) {
  const { item, meds, med, tab } = row;
  const isPair = isPairItem(meds);
  const last = lastResponse(item);

  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-sm" aria-label="Review details">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F36F8F]">
            <Icon icon="lucide:pill" className="h-5 w-5 text-white" />
          </div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-extrabold text-[#0A2947]">{med.name}</h2>
            {med.status === "active" && (
              <span className="rounded-md bg-[#E4002B]/70 px-2 py-0.5 text-[10px] font-bold text-white">Active</span>
            )}
            {!isPair && <RxcuiTag rxcui={med.rxcui} />}
          </div>
        </div>
        <Button variant="link" onClick={onClose} aria-label="Close" className="!h-auto !p-0 !text-[#9CA3AF] hover:!text-[#111827]">
          <Icon icon="lucide:x" className="h-4 w-4" />
        </Button>
      </div>
      <p className="mt-1 pl-14 text-xs text-[#9CA3AF]">Flagged {formatDaysAgo(daysSince(item.createdAt))}</p>

      <div className="mt-5 rounded-xl border border-[#F8C9D2] bg-[#FFF5F7] p-4">
        <p className="text-xs font-extrabold tracking-wide text-[#E4002B]">REASON:</p>
        <p className="mt-1 text-sm font-semibold text-[#0A2947]">{reviewTitle(meds)}</p>
        {reviewTitle(meds) !== ISSUE_LABELS[item.triggerType] && (
          <p className="mt-0.5 text-sm text-[#4B5563]">{ISSUE_LABELS[item.triggerType]}</p>
        )}
      </div>

      {((item.clinicianQuestions ?? []).length > 0 || item.clinicianQuestion) && (
        <PatientConversation
          className="mt-5"
          messages={itemConversation(item, isPair)}
          viewer="patient"
          maxVisible={3}
          waitingFor={item.itemStatus === "NEEDS_MORE_INFO" ? "patient" : null}
        />
      )}

      <div className="mt-5 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4">
        <h3 className="mb-4 text-sm font-extrabold text-[#0A2947]">Extracted Information</h3>
        {isPair ? (
          // Two records that disagree: the same field grid for each, side by
          // side, with the fields that differ marked.
          <PairFields meds={meds} />
        ) : (
          <FieldGrid med={med} columns="grid-cols-2 sm:grid-cols-3" />
        )}
      </div>

      {tab === "flagged" ? (
        <div className="mt-5 border-t border-[#E5E7EB] pt-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {item.clinicianQuestion ? (
              <div>
                <p className="text-sm font-semibold text-[#111827]">Your clinician asked you a question</p>
                <p className="text-xs text-[#6B7280]">Reply so they can finish reviewing this medicine.</p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-semibold text-[#111827]">Please review</p>
                <p className="text-xs text-[#6B7280]">Your clinician makes the final decision either way.</p>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button
                onClick={() => onReview(item.id)}
                disabled={skipping}
                className="!flex !h-auto !items-center !gap-1.5 !rounded-lg !bg-[#0A2947] !px-4 !py-2 !text-sm !font-semibold !text-white hover:!bg-[#0A2947] hover:!opacity-90"
              >
                <Icon icon={item.clinicianQuestion ? "lucide:reply" : "lucide:clipboard-pen"} className="h-4 w-4 !text-white" />
                {item.clinicianQuestion ? "Reply" : "Review"}
              </Button>
              {/* While skipping: unclickable, spinner centred, same size (label kept but hidden). */}
              <Button
                variant="outline"
                disabled={skipping}
                aria-busy={skipping}
                onClick={() => onSkip(item.id)}
                className="!relative !h-auto !rounded-lg !border-[#0A2947] !px-4 !py-2 !text-sm !font-semibold !text-[#0A2947] hover:!border-[#0A2947] hover:!bg-[#F9FAFB] hover:!text-[#0A2947]"
              >
                <span className={cn(skipping && "invisible")}>Skip, let clinician decide</span>
                {skipping && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <LoadingOutlined aria-hidden />
                  </span>
                )}
              </Button>
            </div>
          </div>
          {/* What each choice does, in plain words. */}
          <ul className="!mb-0 !mt-4 grid gap-2 !pl-0 text-xs text-[#4B5563]" aria-label="What happens next">
            <li className="flex items-start gap-2">
              <Icon icon="lucide:clipboard-pen" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#0A2947]" />
              {item.clinicianQuestion ? (
                <span><span className="font-semibold text-[#0A2947]">Reply:</span> answer your clinician's question — your reply goes straight to them.</span>
              ) : (
                <span><span className="font-semibold text-[#0A2947]">Review:</span> tell us which is right and add a comment — your answer goes to your clinician with this record.</span>
              )}
            </li>
            <li className="flex items-start gap-2">
              <Icon icon="lucide:skip-forward" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#6B7280]" />
              <span><span className="font-semibold text-[#0A2947]">Skip:</span> it goes to your clinician as it is, without any answer or comment from you.</span>
            </li>
          </ul>
        </div>
      ) : tab === "reviewed" ? (
        <div className="mt-5 flex items-center justify-between gap-3 border-t border-[#E5E7EB] pt-5">
          <p className="text-sm text-[#6B7280]">{last ? answeredText(last.answer) : "—"}</p>
          <span className={cn("rounded-full px-3 py-1 text-xs font-semibold", outcomeTag(item).className)}>{outcomeTag(item).label}</span>
        </div>
      ) : (
        <div className="mt-5 flex items-center justify-between gap-3 border-t border-[#E5E7EB] pt-5">
          <p className="text-sm text-[#6B7280]">{skippedText(item)}</p>
          {item.canRespond ? (
            <Button
              onClick={() => onReview(item.id)}
              className="!h-auto !shrink-0 !rounded-lg !bg-[#0A2947] !px-4 !py-2 !text-sm !font-semibold !text-white hover:!bg-[#0A2947] hover:!opacity-90"
            >
              Add your answer
            </Button>
          ) : (
            <span className={cn("shrink-0 rounded-full px-3 py-1 text-xs font-semibold", outcomeTag(item).className)}>{outcomeTag(item).label}</span>
          )}
        </div>
      )}
    </div>
  );
}
